/**
 * Returns suggestions for the token currently being typed.
 * Matches both command values, like /snapshot, and labels, like Snapshot.
 */
export function filterEditableOptions(activeToken, options) {
  if (!activeToken) return []
  const token = activeToken.text.toLowerCase()
  const query = token.slice(1)
  return options.filter(item => {
    const command = (item.command || item.value || '').toLowerCase()
    const title = (item.title || item.label || '').toLowerCase()
    return command.startsWith(token) || (command.startsWith(token[0]) && title.includes(query))
  })
}

/**
 * Finds the unfinished /command or @source directly before the caret.
 * Returns token text plus start/end indexes in the plain-text editor value.
 */
export function findActiveToken(text, caret) {
  const left = text.slice(0, caret)
  const match = left.match(/(^|\s)([/@][^\s]*)$/)
  return match ? {text: match[2], start: caret - match[2].length, end: caret} : undefined
}

export class EditableEditor {
  constructor(editor, options) {
    this.editor = editor
    this.options = options
  }

  get text() {
    let text = ''
    this.editor?.childNodes.forEach(node => {
      text += this.textFromNode(node)
    })
    return text.replace(/\u00a0/g, ' ')
  }

  get caret() {
    const selection = window.getSelection()
    if (!this.editor || !selection.rangeCount) return 0

    const range = selection.getRangeAt(0)
    const clone = range.cloneRange()
    clone.selectNodeContents(this.editor)
    clone.setEnd(range.endContainer, range.endOffset)

    let offset = 0
    clone.cloneContents().childNodes.forEach(node => {
      offset += this.textFromNode(node).length
    })
    return offset
  }

  get active_token() {
    return findActiveToken(this.text, this.caret)
  }

  render(text, caret = text.length) {
    if (!this.editor) return

    this.editor.innerHTML = this.htmlFromText(text, this.active_token)
    this.setCaret(Math.min(caret, text.length))
  }

  setCaret(offset) {
    if (!this.editor) return

    const selection = window.getSelection()
    const range = document.createRange()
    let current = 0

    const walk = node => {
      if (node.nodeType === Node.TEXT_NODE) {
        const next = current + node.textContent.length
        if (offset <= next) {
          range.setStart(node, Math.max(0, offset - current))
          range.collapse(true)
          return true
        }

        current = next
        return false
      }

      if (node.nodeType !== Node.ELEMENT_NODE) return false
      if (this.isSuggestionsNode(node)) return false

      if (this.isChipNode(node)) {
        const value = node.dataset.value || node.textContent
        const next = current + value.length
        if (offset <= next) {
          range.setStartAfter(node)
          range.collapse(true)
          return true
        }

        current = next
        return false
      }

      for (const child of node.childNodes) {
        if (walk(child)) return true
      }

      return false
    }

    if (!walk(this.editor)) {
      range.selectNodeContents(this.editor)
      range.collapse(false)
    }

    selection.removeAllRanges()
    selection.addRange(range)
  }

  moveCaretToEnd() {
    this.setCaret(this.text.length)
  }

  insertText(text, maxlength) {
    let next = text

    if (maxlength !== undefined) {
      const remaining = Math.max(maxlength - this.text.length, 0)
      next = text.slice(0, remaining)
    }

    document.execCommand('insertText', false, next)
  }

  removeChipBeforeCaret() {
    const selection = window.getSelection()
    if (!this.editor || !selection.rangeCount || !selection.isCollapsed) return undefined

    const range = selection.getRangeAt(0)
    let node = range.startContainer
    const offset = range.startOffset

    if (node === this.editor) {
      node = this.editor.childNodes[offset - 1]
    }
    else if (node.nodeType === Node.TEXT_NODE && offset === 0) {
      node = node.previousSibling
    }
    else {
      return undefined
    }

    if (!this.isChipNode(node)) return undefined
    return this.removeChipNode(node)
  }

  removeChipNode(node){
    this.cleanChipBoundarySpaces(node)
    return this.removeNode(node)
  }

  cleanChipBoundarySpaces(node) {
    const previous = node.previousSibling
    const next = node.nextSibling
    const has_previous_text = previous?.nodeType === Node.TEXT_NODE
    const has_next_text = next?.nodeType === Node.TEXT_NODE

    if (has_previous_text && has_next_text) {
      if (/\s$/.test(previous.textContent) && /^\s/.test(next.textContent)) {
        next.textContent = next.textContent.replace(/^\s+/, '')
      }
      return
    }

    if (!previous && has_next_text) {
      next.textContent = next.textContent.replace(/^\s+/, '')
    }
    else if (!next && has_previous_text) {
      previous.textContent = previous.textContent.replace(/\s+$/, '')
    }
  }

  removeNode(node){
    node.remove()
    return {
      caret: this.caret,
      text: this.text
    }
  }

  htmlFromText(text, activeToken) {
    const value = String(text)
    const pattern = /(^|\s)([/@][^\s]*)/g
    let html = ''
    let index = 0
    let match

    while ((match = pattern.exec(value))) {
      const full = match[0]
      const prefix = match[1]
      const command = match[2]
      const commandStart = match.index + prefix.length
      const option = this.options.find(item => item.command === command)

      html += this.escapeHtml(value.slice(index, commandStart))

      if (option) {
        html += `<span class="chip-wrapper ${option.type || ''}" contenteditable="false" data-value="${this.escapeAttr(command)}"></span>`
      }
      else if (activeToken?.start === commandStart && activeToken.end === commandStart + command.length) {
        html += '<span class="active-token-wrapper">'
        html += this.escapeHtml(command)
        html += '<span class="suggestions-mount"></span></span>'
      }
      else {
        html += this.escapeHtml(command)
      }

      index = match.index + full.length
    }

    html += this.escapeHtml(value.slice(index))
    return html
  }

  textFromNode(node) {
    if (node.nodeType === Node.TEXT_NODE) return node.textContent
    if (node.nodeType !== Node.ELEMENT_NODE) return ''
    if (this.isSuggestionsNode(node)) return ''
    if (this.isChipNode(node)) return node.dataset.value || node.textContent
    if (node.tagName === 'BR') return '\n'

    let text = ''
    node.childNodes.forEach(child => {
      text += this.textFromNode(child)
    })
    return text
  }

  isSuggestionsNode(node) {
    return node?.nodeType === Node.ELEMENT_NODE
      && (node.classList.contains('suggestions-mount') || node.classList.contains('suggestions'))
  }

  isChipNode(node) {
    return node?.nodeType === Node.ELEMENT_NODE
      && (node.classList.contains('chip') || node.classList.contains('chip-wrapper') || Boolean(node.dataset.value))
  }

  escapeHtml(value) {
    return String(value)
      .replaceAll('&', '&amp;')
      .replaceAll('<', '&lt;')
      .replaceAll('>', '&gt;')
      .replaceAll('"', '&quot;')
      .replace(/'/g, '&#039;')
  }

  escapeAttr(value) {
    return this.escapeHtml(value).replaceAll('"', '&quot;')
  }
}
